/**
 * Admin layout — distinct treatment from main AppLayout.
 *
 * Uses a dark header bar with "Admin" badge and admin-specific sidebar.
 * Mobile-first: sidebar is a drawer overlay on small screens.
 */

import { useState } from "react";
import { NavLink, Outlet, useLocation } from "react-router-dom";
import {
  CaretLeft as ChevronLeft,
  Cpu,
  FileText,
  SquaresFour as LayoutDashboard,
  List as Menu,
  ChatCircleDots as MessageSquare,
  Scroll as ScrollText,
  Shield,
  Terminal,
  Users,
  X,
} from "@phosphor-icons/react";
import { cn } from "@/utils/cn";
import { getPlatformRole, hasRole } from "@/components/auth/AdminGuard";
import { config } from "@/config";
import { Badge } from "@/components/ui/Badge";

interface AdminNavItem {
  to: string;
  icon: React.ElementType;
  label: string;
  /** Roles that can see this item */
  roles?: ("platform_admin" | "platform_support" | "platform_analyst")[];
}

// Paths are relative to this app's own root ("/"), not "/app/admin/*" — the
// admin app has no "/app" of its own; everything it serves is the console.
const adminNav: AdminNavItem[] = [
  { to: "/", icon: LayoutDashboard, label: "Overview" },
  {
    to: "/users",
    icon: Users,
    label: "Users",
    roles: ["platform_admin", "platform_support"],
  },
  {
    to: "/content",
    icon: FileText,
    label: "Content",
    roles: ["platform_admin", "platform_support"],
  },
  {
    to: "/feedback",
    icon: MessageSquare,
    label: "Feedback",
    roles: ["platform_admin", "platform_support"],
  },
  {
    to: "/ai-usage",
    icon: Cpu,
    label: "Usage & Cost",
    roles: ["platform_admin", "platform_analyst"],
  },
  {
    to: "/audit",
    icon: ScrollText,
    label: "Audit Logs",
    roles: ["platform_admin"],
  },
  {
    to: "/logs",
    icon: Terminal,
    label: "CloudWatch Logs",
    roles: ["platform_admin"],
  },
  {
    to: "/internal-interviews",
    icon: Users,
    label: "Interviews",
    roles: ["platform_admin", "platform_support"],
  },
];

function isNavActive(to: string, pathname: string) {
  if (to === "/") return pathname === "/";
  return pathname.startsWith(to);
}

export function AdminLayout() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const role = getPlatformRole();

  const visibleNav = adminNav.filter(
    (item) => !item.roles || hasRole(...item.roles)
  );

  // The "set up an authenticator app" nudge that used to live here is gone:
  // AdminMfaGate (packages/admin/src/components/auth/AdminMfaGate.tsx), one
  // layer up in the route tree, now forces enrolment to completion before
  // this component — or any admin page — ever renders. By the time
  // AdminLayout mounts, the signed-in admin is always enrolled; there is no
  // "not yet" state left for a banner to show.

  const sidebar = (
    <nav className="flex flex-col gap-1 px-2 py-4">
      {visibleNav.map((item) => {
        const Icon = item.icon;
        const active = isNavActive(item.to, location.pathname);
        return (
          <NavLink
            key={item.to}
            to={item.to}
            onClick={() => setMobileOpen(false)}
            className={cn(
              "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors",
              active
                ? "bg-primary/10 text-primary"
                : "text-muted-foreground hover:bg-accent hover:text-foreground"
            )}
          >
            <Icon className="h-4 w-4 shrink-0" />
            {item.label}
          </NavLink>
        );
      })}
    </nav>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* ── Admin header ── */}
      <header className="fixed top-0 left-0 right-0 z-40 h-14 border-b border-border bg-zinc-900 text-white">
        <div className="flex h-full items-center justify-between px-4">
          <div className="flex items-center gap-3">
            {/* Mobile menu toggle */}
            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="rounded-md p-1.5 hover:bg-white/10 md:hidden"
              aria-label="Toggle admin menu"
            >
              {mobileOpen ? (
                <X className="h-5 w-5" />
              ) : (
                <Menu className="h-5 w-5" />
              )}
            </button>

            <div className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-amber-400" />
              <span className="text-sm font-semibold tracking-tight">
                Spark Admin
              </span>
              {config.environmentBadge && (
                <Badge
                  variant="warning"
                  className="px-1.5 py-0 text-[10px] leading-4"
                >
                  {config.environmentBadge}
                </Badge>
              )}
              {role && (
                <span className="hidden rounded-full bg-amber-400/20 px-2 py-0.5 text-xs font-medium text-amber-300 sm:inline-block">
                  {role.replace("platform_", "")}
                </span>
              )}
            </div>
          </div>

          {config.appUrl && (
            <a
              href={config.appUrl}
              className="flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-zinc-300 hover:bg-white/10 hover:text-white transition-colors"
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Back to App
            </a>
          )}
        </div>
      </header>

      {/* ── Mobile sidebar drawer ── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        className={cn(
          "fixed top-14 left-0 z-30 h-[calc(100vh-3.5rem)] w-60 border-r border-border bg-card transition-transform duration-200",
          // Mobile: off-screen unless open
          "-translate-x-full md:translate-x-0",
          mobileOpen && "translate-x-0"
        )}
      >
        {sidebar}
      </aside>

      {/* ── Main content ── */}
      <main className="pt-14 md:pl-60">
        <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6 md:px-8 md:py-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
